import { useQuery } from "@tanstack/react-query";
import { getBackendUrl, getEnvParsedSafe } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { Environment, environment } from "app/environment/environment";
import { log, LogLevel } from "logging/datadogBrowserLogs";

type FeatureFlag = {
  name: string;
  enabled: boolean;
};

const checkEnabledLocally = (name: string) => {
  const isDevelopment = environment() === Environment.Development;
  if (!isDevelopment) return false;
  const enabledFlags = getEnvParsedSafe("VITE_ENABLED_FEATURE_FLAGS") ?? [];
  return enabledFlags.includes(name);
};

const enabledFlag = (name: string) => ({ name, enabled: true, attributes: [] });

export function useFeatureFlag(name: string): FeatureFlag {
  const { data, error } = useQuery({
    queryKey: ["featureFlags"],
    queryFn: ({ signal }) =>
      queryFnGET<FeatureFlag[]>({ signal, url: `${getBackendUrl()}/features` }),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const enabledLocally = checkEnabledLocally(name);
  if (enabledLocally) return enabledFlag(name);

  if (error) {
    log(
      LogLevel.error,
      "useFeatureFlag",
      `GET /features call failed with error: ${error}`,
    );
  }

  return (
    (data && data.length ? data.find((flag) => flag.name === name) : null) ?? {
      name,
      enabled: false,
    }
  );
}
